import { getMonth, getYear } from '@sequencehq/utils/dates'

/**
 * Rounds a value up to the next “nice” tick (1 / 2 / 3 / 5 × 10ⁿ).
 * Example: 1380 → 2000, 420 → 500, 7.2 → 10.
 */
const niceCeil = (value: number): number => {
  if (value === 0) {
    return 0
  }

  const abs = Math.abs(value)
  const exponent = Math.floor(Math.log10(abs))
  const magnitude = Math.pow(10, exponent)
  const scaled = abs / magnitude

  let niceScaled: number

  if (scaled <= 1) {
    niceScaled = 1
  } else if (scaled <= 2) {
    niceScaled = 2
  } else if (scaled <= 3) {
    niceScaled = 3
  } else if (scaled <= 5) {
    niceScaled = 5
  } else {
    niceScaled = 10
  }

  const result = niceScaled * magnitude
  return value < 0 ? -result : result
}

/**
 * Produces the three Y-axis labels (bottom, middle, top) for the graph. The
 * axis always has exactly 3 stages with equal steps.
 *
 *   positive:  0 ......... mid ......... max     (anchored at 0)
 *   negative:  min ....... mid ......... 0       (anchored at 0)
 *   crossing: -M ......... 0 ........... M       (symmetric about 0)
 */
export const calculateYAxisLabels = (data: Array<number>): Array<number> => {
  if (data.length === 0) {
    return [0, 0]
  }

  const rawMax = Math.max(...data)
  const rawMin = Math.min(...data)

  // All values >= 0: run 0 -> mid -> max.
  if (rawMin >= 0) {
    const max = Math.max(0, niceCeil(rawMax))
    if (max === 0) {
      return [0, 0.5, 1]
    }
    return [0, max / 2, max]
  }

  // All values <= 0: run min -> mid -> 0.
  if (rawMax <= 0) {
    const min = -niceCeil(-rawMin)
    return [min, min / 2, 0]
  }

  // Values cross zero: use a symmetric axis so the step stays nice and 0 sits
  // on the middle line.
  const magnitude = niceCeil(Math.max(rawMax, -rawMin))
  return [-magnitude, 0, magnitude]
}

export type Bucket = {
  year: number
  month: number
}

/**
 * Given a bucket, return a date
 * @param bucket
 * @returns Date
 */
export const createDateFromBucket = (bucket: Bucket): Date =>
  new Date(
    `${bucket.year}-${bucket.month < 10 ? `0${bucket.month}` : bucket.month}-01`
  )

/**
 * Given a date, return the bucket representation of that date.
 * @param date
 * @returns Bucket
 */
export const createBucketFromDate = (date: Date): Bucket => {
  return {
    year: getYear(date),
    month: getMonth(date) + 1
  }
}

/**
 * For a given start and end bucket, generate all of the intermediate buckets.
 * This is especially useful for filling in the 'blanks' in our XYCharts.
 * @param start
 * @param end
 * @param buckets
 * @returns
 */
export const generateAllBucketsInPeriod = (
  start: { year: number; month: number },
  end: { year: number; month: number },
  buckets: Array<Bucket> = [start]
): Array<Bucket> => {
  const latestBucket = buckets[buckets.length - 1]
  const nextMonth = latestBucket.month === 12 ? 1 : latestBucket.month + 1
  const nextYear =
    latestBucket.month === 12 ? latestBucket.year + 1 : latestBucket.year

  const nextBucket = {
    year: nextYear,
    month: nextMonth
  }

  if (
    nextBucket.year > end.year ||
    (nextBucket.year === end.year && nextBucket.month > end.month)
  ) {
    return buckets
  }

  return generateAllBucketsInPeriod(start, end, [...buckets, nextBucket])
}
